import {
    createPkgValDesc,
    createRefValDesc, IdentifierType, PackageDescription,
    ValueDescription
} from 'monaco-azure-logic-app-lang';
import { GetMetaDataActionOutputFieldNames, GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';

export class GetMetaActivityDescriptionBuilder {

    static FIELDS: GetMetaDataActionOutputFieldNames[] = [
        'structure', 'columnCount', 'exists', 'itemName', 'itemType', 'lastModified', 'childItems', 'size', 'contentMD5', 'created', 'partitionIndexes'
    ];
    static FIELD_INDEX_SET = GetMetaActivityDescriptionBuilder.FIELDS.reduce((previousValue: Map<string, number>, currentValue: string, curIndex: number) => {
        // filter out the first empty string
        if (currentValue) {
            previousValue.set(currentValue, curIndex);
        }
        return previousValue;
    }, new Map<string, number>());
    static FIELD_VD_RECORD: Record<string, ValueDescription> = {
        structure: createRefValDesc(
            [
                'structure:{name, type}[]',
                'Data structure of the file or relational database table. Returned value is a list of column names and column types.'
            ],
            IdentifierType.ARRAY_OF_TYPE(['activityPackage', 'getMetaDataActivity', 'itemType'], 'structure return type')
        ),
        columnCount: createRefValDesc([
            'columnCount:number',
            'Number of columns in the file or relational table.'
        ], IdentifierType.Number),
        exists: createRefValDesc(
            [
                'exists:boolean',
                'Whether a file, folder, or table exists. If exists is specified in the Get Metadata field list, the activity won\'t fail even if the file, folder, or table doesn\'t exist. Instead, exists: false is returned in the output.'
            ],
            IdentifierType.Boolean
        ),
        itemName: createRefValDesc([
            'itemName:string',
            'Name of the file or folder.'
        ], IdentifierType.String),
        itemType: createRefValDesc([
            'itemType:string',
            'Type of the file or folder. Returned value is File or Folder.'
        ], IdentifierType.String),
        lastModified: createRefValDesc([
            'lastModified:string',
            'Last modified datetime of the file or folder.'
        ], IdentifierType.String),
        childItems: createRefValDesc(
            [
                'childItems:{name, type}[]',
                'List of subfolders and files in the given folder. Applicable only to folders. Returned value is a list of the name and type of each child item.'
            ],
            IdentifierType.ARRAY_OF_TYPE(['activityPackage', 'getMetaDataActivity', 'itemType'], 'childItems return type')
        ),
        size: createRefValDesc([
            'size:number',
            'Size of the file, in bytes. Applicable only to files.'
        ], IdentifierType.Number),
        contentMD5: createRefValDesc([
            'contentMD5:string',
            'MD5 of the file. Applicable only to files.'
        ], IdentifierType.String),
        created: createRefValDesc([
            'created:string',
            'Created datetime of the file or folder.'
        ], IdentifierType.String),
        partitionIndexes: createRefValDesc([
            'partitionIndexes:any',
            'Partition indexes'
        ], IdentifierType.Any)
    };

    static SUB_VD_ITEM_TYPE =  createPkgValDesc(['Medata item type'], {
        name: createRefValDesc(['name of the item'], IdentifierType.String),
        type: createRefValDesc(['type of the item data'], IdentifierType.String)
    }, {allowAdditionalAnyProperties: true});
    static SUB_VD_DEFAULT_RETURN_VALUE =  createPkgValDesc(['**Return value of a getMetadata activity**', 'GetMetadata activity'], {
        output: createPkgValDesc(
            [
                'output:any',
                'GetMetadata activity output'
            ],
            GetMetaActivityDescriptionBuilder.FIELD_VD_RECORD,  {allowAdditionalAnyProperties: true}
        )
    }, {allowAdditionalAnyProperties: true});

    private static _cachedPkgValDescByBitwiseValue: Map<number, PackageDescription> = new Map<number, PackageDescription>();

    private constructor() {
    }

    static pack(fields: string[]): number {
        let result = 0;
        for (const oneField of fields) {
            if (this.FIELD_INDEX_SET.has(oneField)) {
                // eslint-disable-next-line no-bitwise
                result |= 1 << this.FIELD_INDEX_SET.get(oneField);
            }
        }
        return result;
    }

    static unpack(value: number): GetMetaDataActionOutputFieldNames[] {
        const result: GetMetaDataActionOutputFieldNames[] = [];
        let index = 0;
        while (value > 0  && index < this.FIELDS.length) {
            // eslint-disable-next-line no-bitwise
            if ((value & 1)) {
                result.push(this.FIELDS[index]);
            }
            // eslint-disable-next-line no-bitwise
            value = value >> 1;
            index++;
        }
        return result;
    }

    static buildSubVdKey(value: number): string {
        return `retVal${value}`;
    }
    static buildPkgValDesc(values: Set<number>): PackageDescription {
        const getMetaDataActivitySubDesc: Record<string, ValueDescription> = {
            itemType: this.SUB_VD_ITEM_TYPE,
            defaultReturnValue: this.SUB_VD_DEFAULT_RETURN_VALUE
        };
        for (const oneVal of values) {

            if (this._cachedPkgValDescByBitwiseValue.has(oneVal)) {
                getMetaDataActivitySubDesc[this.buildSubVdKey(oneVal)] = this._cachedPkgValDescByBitwiseValue.get(oneVal);
                continue;
            }

            const oneFieldArr = this.unpack(oneVal);
            if (oneFieldArr.length) {
                // okay, we need to populate few fields within the description

                const oneOutputSubVd: Record<string, ValueDescription> = {};
                for (const oneField of oneFieldArr) {
                    if (this.FIELD_INDEX_SET.has(oneField)) {
                        oneOutputSubVd[oneField] = this.FIELD_VD_RECORD[oneField];
                    }
                }

                const onePkgVd = createPkgValDesc(['**Return value of a getMetadata activity**', 'GetMetadata activity'], {
                    output: createPkgValDesc(
                        [
                            'output:any',
                            `GetMetadata activity output`
                        ],
                        oneOutputSubVd,  {allowAdditionalAnyProperties: true}
                    )
                }, {allowAdditionalAnyProperties: true});

                getMetaDataActivitySubDesc[this.buildSubVdKey(oneVal)] = onePkgVd;

                // cache it for the next time
                this._cachedPkgValDescByBitwiseValue.set(oneVal, onePkgVd);
            }
            // if no fields needed, need not create vd; thus it use defaultActivity pkgVd
        }
        return createPkgValDesc(['Package GetMetadata activity'], getMetaDataActivitySubDesc);
    }

    static getMetadataActivityFileDatasetBitwiseFields(getMetadataActivity: GetMetadataActivity): number {
        return this.pack(Array.from(getMetadataActivity.getFieldNameSet()));
    }
}
